import { createContext, useContext, useState, useEffect } from "react";
import { getMe } from "../api/user";
import { authStore } from "../api/client";
import i18n from "../i18n/i18n";
import BASE_URL from "../api/baseUrl";

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyUser = (userData) => {
    setUser(userData);
    if (userData?.locale) i18n.changeLanguage(userData.locale);
  };

  const login = async (token) => {
    setAccessToken(token);
    authStore.setToken(token);
    try {
      const userData = await getMe();
      applyUser(userData);
    } catch (err) {
      console.error("Failed to load user", err);
      setUser(null);
    }
  };

  const logout = async () => {
    setAccessToken(null);
    setUser(null);
    authStore.clear();
    await fetch(`${BASE_URL}/auth/logout`, { method: "POST", credentials: "include" });
  };

  // If a mid-session token refresh fails (refresh token expired or revoked),
  // reset auth state so the UI shows the logged-out view instead of errors.
  useEffect(() => {
    authStore.setOnSessionExpired(() => {
      setAccessToken(null);
      setUser(null);
    });
  }, []);

  // Silent refresh on app load
  useEffect(() => {
    const refresh = async () => {
      try {
        const res = await fetch(`${BASE_URL}/auth/refresh`, { method: "POST", credentials: "include" });
        if (res.ok) {
          const { access_token } = await res.json();
          setAccessToken(access_token);
          authStore.setToken(access_token);
          const userData = await getMe();
          applyUser(userData);
        }
      } catch (err) {
        console.log("Silent refresh failed");
      } finally {
        setLoading(false);
      }
    };
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ accessToken, user, login, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
